import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Field,
  Input,
  Select,
  useToast,
} from "../../../design-system";
import EmployeePicker from "../../../Components/Kronaly/Empleados/EmployeePicker";
import { uploadDocument } from "../../../helpers/documents_helper";
import { TEXTAREA_STYLE } from "../constants";
import { FileDropInput } from "./FileDropInput";

export function UploadDocumentDrawer({
  open,
  onClose,
  employees,
  categories = [],
  defaultEmployeeProfileId = "",
  onUploaded,
}) {
  const toast = useToast();
  const fileRef = useRef(null);
  const initialForm = () => ({
    title: "",
    description: "",
    category_root_id: "",
    category_sub_id: "",
    visibility: "employee",
    employee_profile_id: defaultEmployeeProfileId || "",
    publish: true,
    expires_at: "",
  });
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm((prev) => ({
        ...prev,
        employee_profile_id: defaultEmployeeProfileId || prev.employee_profile_id,
        category_root_id: prev.category_root_id || categories?.[0]?.id || "",
      }));
    } else {
      setForm(initialForm());
      setFile(null);
      setError(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open, defaultEmployeeProfileId, categories]);

  const rootCategory = (categories || []).find((c) => c.id === form.category_root_id) || null;
  const subOptions = rootCategory?.children || [];

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f && !form.title.trim()) {
      const nameWithoutExt = f.name.replace(/\.[^/.]+$/, "");
      update({ title: nameWithoutExt });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!file) {
      setError("Selecciona un archivo.");
      return;
    }
    if (!form.title.trim()) {
      setError("El título es obligatorio.");
      return;
    }
    if (form.visibility === "employee" && !form.employee_profile_id) {
      setError("Selecciona un empleado para la visibilidad 'Empleado'.");
      return;
    }
    const categoryId = form.category_sub_id || form.category_root_id;
    if (!categoryId) {
      setError("Selecciona una categoría.");
      return;
    }

    setSubmitting(true);
    try {
      await uploadDocument({
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        category_id: categoryId,
        visibility: form.visibility,
        employee_profile_id:
          form.visibility === "workspace" ? undefined : form.employee_profile_id || undefined,
        publish: form.publish,
        expires_at: form.expires_at || undefined,
        file,
      });
      toast.success("Documento subido");
      onUploaded?.();
    } catch (e2) {
      setError(typeof e2 === "string" ? e2 : e2?.message || "No se pudo subir el documento.");
    } finally {
      setSubmitting(false);
    }
  };

  const formId = "upload-document-form";

  return (
    <Drawer
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Subir documento"
      size="md"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="primary" type="submit" form={formId} icon="Upload" disabled={submitting}>
            {submitting ? "Subiendo…" : "Subir documento"}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="Archivo" required>
          <FileDropInput
            file={file}
            inputRef={fileRef}
            onFileChange={onFileChange}
          />
        </Field>

        <Field label="Título" required>
          <Input
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Ej. Contrato indefinido 2026"
            required
          />
        </Field>

        <Field label="Descripción">
          <textarea
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Opcional — contexto interno del documento."
            rows={3}
            style={TEXTAREA_STYLE}
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Categoría" required>
            <Select
              value={form.category_root_id}
              onChange={(e) => update({ category_root_id: e.target.value, category_sub_id: "" })}
            >
              <option value="">— Selecciona —</option>
              {(categories || []).map((root) => (
                <option key={root.id} value={root.id}>
                  {root.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Subcategoría"
            help={subOptions.length === 0 ? "Sin subcategorías." : undefined}
          >
            <Select
              value={form.category_sub_id}
              onChange={(e) => update({ category_sub_id: e.target.value })}
              disabled={subOptions.length === 0}
            >
              <option value="">— Ninguna —</option>
              {subOptions.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Visibilidad" required>
          <Select
            value={form.visibility}
            onChange={(e) => update({ visibility: e.target.value })}
          >
            <option value="employee">Empleado</option>
            <option value="workspace">Workspace</option>
            <option value="private">Privado (solo managers)</option>
          </Select>
        </Field>

        {form.visibility !== "workspace" && (
          <Field
            label="Empleado"
            required={form.visibility === "employee"}
            help={
              form.visibility === "private"
                ? "Opcional. Asocia el documento a un empleado sin hacerlo visible para él."
                : undefined
            }
          >
            <EmployeePicker
              employees={employees}
              value={form.employee_profile_id}
              onChange={(id) => update({ employee_profile_id: id })}
            />
          </Field>
        )}

        <Field
          label="Fecha de caducidad"
          help="Opcional. Útil para certificados, DNI, permisos, justificantes con vencimiento."
        >
          <Input
            type="date"
            value={form.expires_at}
            onChange={(e) => update({ expires_at: e.target.value })}
          />
        </Field>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--kr-fg-strong)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={form.publish}
            onChange={(e) => update({ publish: e.target.checked })}
          />
          Publicar al subir (si no, queda como borrador)
        </label>
      </form>
    </Drawer>
  );
}
